/**
 * `MarketLifecycle` → proposal-row `status` mapper (proposals variant).
 *
 * Mirrors `mapLifecycleStatus` (markets variant) but uses the proposal
 * status vocabulary from the API:
 *
 *   ['active', 'needs_debate', 'pending_review', 'creating', 'live', 'rejected']
 *
 * Proposals are markets in `status=backing`. The launchpad-feed `excludeStatus`
 * filter partitions Popular/Newest tabs (excludes 'live') from the Graduated
 * tab (only 'live'). When a proposal graduates (TRADING_STARTED / GRADUATION)
 * the row's status flips to `'live'` and its canonical market link is set to
 * the same pubkey; no row removal needed — client-side filtering moves it
 * between tabs naturally.
 *
 * LifecycleKind → status mapping (numbers match `morfi.v1.LifecycleKind`):
 *
 *   | LifecycleKind    | value | → status      | rationale                          |
 *   |------------------|-------|---------------|------------------------------------|
 *   | UNSPECIFIED      | 0     | (no change)   | not a real transition              |
 *   | BACKING_STARTED  | 1     | (no change)   | proposal is born in this state     |
 *   | TRADING_STARTED  | 2     | 'live'        | graduated to market trading        |
 *   | LEADER_CHANGED   | 3     | (no change)   | leader flip ≠ a proposal-state flip |
 *   | GRADUATION       | 5     | 'live'        | graduated to AMM                   |
 *
 * TRADING_STARTED and GRADUATION already cover the graduated state and
 * always precede resolution; RESOLUTION is no longer on lifecycle.v0.
 *
 * Immutable: returns a NEW object only when the lifecycle update changes the
 * status or repairs a missing canonical market link; otherwise the exact
 * `prev` reference.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import { sdkEvents } from '../core/events.js';

const KIND_TRADING_STARTED = 2;
const KIND_GRADUATION = 5;

interface ProposalLifecycleRow {
  readonly pubkey?: string;
  readonly status?: string;
  readonly graduatedMarketPubkey?: string | null;
}

function coerceKind(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (/^\d+$/.test(trimmed)) return Number(trimmed);
    switch (trimmed) {
      case 'TRADING_STARTED':
      case 'LIFECYCLE_KIND_TRADING_STARTED':
        return KIND_TRADING_STARTED;
      case 'GRADUATION':
      case 'LIFECYCLE_KIND_GRADUATION':
        return KIND_GRADUATION;
      default:
        return 0;
    }
  }
  return 0;
}

function statusForKind(kind: number): string | null {
  switch (kind) {
    case KIND_TRADING_STARTED:
    case KIND_GRADUATION:
      return 'live';
    default:
      return null;
  }
}

export function mapProposalLifecycleStatus<T extends ProposalLifecycleRow>(
  prev: T,
  raw: Record<string, unknown>,
): T {
  const status = statusForKind(coerceKind(raw.kind));
  if (status === null) return prev;

  const graduatedMarketPubkey = marketPubkeyFor(prev);
  if (prev.status === status && prev.graduatedMarketPubkey === graduatedMarketPubkey) {
    return prev;
  }

  return { ...prev, status, graduatedMarketPubkey };
}

/** Graduation is irreversible, even when a backing refresh finishes after the event. */
export function preserveProposalGraduation<T extends object>(previous: unknown, next: T): T {
  if (
    previous === null ||
    typeof previous !== 'object' ||
    !('status' in previous) ||
    previous.status !== 'live'
  )
    return next;
  const prior = previous as ProposalLifecycleRow;
  const incoming = next as ProposalLifecycleRow;
  const graduatedMarketPubkey = marketPubkeyFor(prior);
  if (
    (incoming.status === undefined || incoming.status === 'live') &&
    (incoming.graduatedMarketPubkey === undefined ||
      incoming.graduatedMarketPubkey === graduatedMarketPubkey)
  )
    return next;
  sdkEvents.emit('applyEvent:error', {
    specName: 'useGetProposal',
    error: 'proposal.snapshot_graduation_regression',
  });
  return { ...next, status: 'live', graduatedMarketPubkey };
}

function marketPubkeyFor(prev: ProposalLifecycleRow): string | null {
  if (isNonEmptyString(prev.graduatedMarketPubkey)) {
    return prev.graduatedMarketPubkey;
  }
  return isNonEmptyString(prev.pubkey) ? prev.pubkey : null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}
